/**
 * Cars with a job in them (order 538; definitions in `carJobs.ts`, docs/ACTIVITIES-OFFLINE.md). Keeps the
 * six motors parked at their kerbs while you are in the area (spawned 60–140 m away, so none appears in
 * front of you; cleared again past 220 m), and plays the offer: get in at wanted level 0 with no job
 * running and the note or the voice speaks; stay in for `CAR_JOB_OFFER_WINDOW` seconds and the job starts,
 * get out and it's a no (ask again by getting back in). A job done once never comes back.
 *
 * Offline only, after the street jobs open (Borrowed Time). Online the motors are never parked and the
 * mission system refuses to start anything anyway.
 */
import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import type { System } from '../core/system';
import type { BookEntry } from '../book/bookScreen';
import type { MissionDef } from '../missions/types';
import { UNLOCK_STREET_JOBS } from '../missions/ids';
import type { MapMarker } from '../ui/uiTypes';
import type { Vehicle } from '../vehicles/types';
import { CAR_JOBS, CAR_JOB_OFFER_WINDOW, CAR_JOB_TAG, carJobDef, type CarJob } from './carJobs';
import { kerbStop } from './roadLine';

/** Parked once you're within this distance (and further than `SPAWN_MIN`, out of the picture). */
export const CAR_JOB_SPAWN_MAX = 140;
export const CAR_JOB_SPAWN_MIN = 60;
/** Cleared past this distance (unless you're in it). */
export const CAR_JOB_CLEAR = 220;
/** The "?" shows on the minimap only this close (they're meant to be come across). */
export const CAR_JOB_MAP_RANGE = 60;

declare module '../core/hooks' {
  interface BhHooks {
    /** Cars with a job: each one's spot, its motor (-1 = not parked), done, and the offer in progress. */
    carJobState(): { jobs: { id: string; x: number; z: number; vehicle: number; done: boolean }[]; offer: { id: string; left: number } | null };
  }
}

interface Slot {
  job: CarJob;
  def: MissionDef;
  spot: { pos: V2; heading: number };
  vehicleId: number;
}

export function createCarJobSystem(): System {
  let ctx: GameContext;
  const slots: Slot[] = [];
  /** Offered to the mission system only while the offer is being taken (like the half-bricks). */
  let offering: MissionDef | null = null;
  /** The offer being made: which car, seconds left to get out. */
  let offer: { slot: Slot; left: number } | null = null;
  /** The motor the current offer (or refusal) was made in: one offer per time you get in. */
  let askedIn = -1;
  const offs: (() => void)[] = [];

  const offline = (): boolean => !ctx.services.tryGet('net')?.online && (ctx.services.tryGet('economy')?.authority ?? 'local') === 'local';
  const save = () => ctx.services.get('save');
  const ui = () => ctx.services.tryGet('ui');
  const open = (): boolean => offline() && save().hasUnlock(UNLOCK_STREET_JOBS);
  const done = (s: Slot): boolean => save().isMissionComplete(s.def.id);

  function carOf(s: Slot): Vehicle | null {
    if (s.vehicleId < 0) return null;
    const v = ctx.services.get('vehicles').get(s.vehicleId);
    if (!v || !v.tags.has(`${CAR_JOB_TAG}:${s.job.id}`)) {
      s.vehicleId = -1;
      return null;
    }
    return v;
  }

  function buildSlots(): void {
    const layout = ctx.services.get('layout');
    for (const job of CAR_JOBS) {
      const poi = layout.pois.find((p) => p.id === job.near);
      if (!poi) continue;
      // The nearest kerbside parking spot, else a stop at the kerb by the door.
      let spot: { pos: V2; heading: number } | null = null;
      let bd = 60;
      for (const p of layout.parkedSpots) {
        const d = distance(p.pos, poi.pos);
        if (d < bd) {
          bd = d;
          spot = { pos: p.pos, heading: p.heading };
        }
      }
      const slot: Slot = { job, def: null as unknown as MissionDef, spot: spot ?? { pos: kerbStop(layout, poi.pos), heading: 0 }, vehicleId: -1 };
      slot.def = carJobDef(
        job,
        () => carOf(slot),
        () => slot.spot.pos,
      );
      slots.push(slot);
    }
  }

  function park(s: Slot): void {
    const vehicles = ctx.services.get('vehicles');
    const player = ctx.services.get('player');
    for (const q of vehicles.query(s.spot.pos, 4.5)) if (q !== player.vehicle && !q.isPolice && !q.tags.has('mission')) vehicles.despawn(q);
    const v = vehicles.spawn(s.job.spec, s.spot.pos, {
      heading: s.spot.heading,
      parked: true,
      ...(s.job.paint !== undefined ? { paint: s.job.paint } : {}),
      tags: [CAR_JOB_TAG, `${CAR_JOB_TAG}:${s.job.id}`],
    });
    s.vehicleId = v.id;
  }

  /** Done (or a new game): the motor becomes an ordinary car where it stands. */
  function letGo(s: Slot): void {
    const v = carOf(s);
    if (v) {
      v.tags.delete(CAR_JOB_TAG);
      v.tags.delete(`${CAR_JOB_TAG}:${s.job.id}`);
    }
    s.vehicleId = -1;
  }

  function keepParked(): void {
    const player = ctx.services.get('player');
    const vehicles = ctx.services.get('vehicles');
    for (const s of slots) {
      if (done(s)) {
        if (s.vehicleId >= 0 && ctx.state.activeMissionId !== s.def.id) letGo(s);
        continue;
      }
      const v = carOf(s);
      const d = distance(player.pos, s.spot.pos);
      if (!v) {
        if (d >= CAR_JOB_SPAWN_MIN && d <= CAR_JOB_SPAWN_MAX && ctx.state.activeMissionId === null) park(s);
        continue;
      }
      if (v.destroyed) continue;
      if (player.vehicle !== v && !v.tags.has('mission') && distance(player.pos, v.pos) > CAR_JOB_CLEAR) {
        vehicles.despawn(v);
        s.vehicleId = -1;
      }
    }
  }

  function takeOffer(s: Slot): void {
    offering = s.def;
    const ok = ctx.services.get('missions').start(s.def.id);
    offering = null;
    if (!ok) ui()?.toast('Not now — finish what you’re on first.', 'warn', 3);
  }

  function offers(dt: number): void {
    const player = ctx.services.get('player');
    const pv = player.vehicle;
    if (!pv || pv.id !== askedIn) {
      if (offer && (!pv || pv !== carOf(offer.slot))) {
        // Out before the time was up: a no.
        ui()?.say(offer.slot.job.voice, offer.slot.job.refuse, 3);
        offer = null;
      }
      if (!pv) askedIn = -1;
    }
    if (offer) {
      offer.left -= dt;
      if (offer.left <= 0) {
        const s = offer.slot;
        offer = null;
        takeOffer(s);
      }
      return;
    }
    if (!pv || pv.id === askedIn || ctx.state.activeMissionId !== null || ctx.state.heat > 0) return;
    const s = slots.find((q) => q.vehicleId === pv.id && !done(q));
    if (!s) return;
    askedIn = pv.id;
    offer = { slot: s, left: CAR_JOB_OFFER_WINDOW };
    ui()?.say(s.job.voice, s.job.offer, CAR_JOB_OFFER_WINDOW + 3);
    ui()?.toast('A job comes with this car. Stay in to take it — get out to say no.', 'info', CAR_JOB_OFFER_WINDOW);
  }

  function markers(out: MapMarker[]): void {
    if (!open() || ctx.state.activeMissionId !== null || (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused')) return;
    const me = ctx.services.get('player').pos;
    for (const s of slots) {
      const v = carOf(s);
      if (!v || done(s) || distance(me, v.pos) > CAR_JOB_MAP_RANGE || ctx.services.get('player').vehicle === v) continue;
      out.push({ x: v.pos.x, z: v.pos.z, kind: 'friend', glyph: '?', pin: false, label: 'A car with a job in it' });
    }
  }

  function jobsEntries(): BookEntry[] {
    if (!open()) return [];
    const finished = slots.filter(done);
    return [
      {
        id: 'side:car-jobs',
        title: `Cars with a job in them — ${finished.length} of ${slots.length}`,
        sub: 'Parked about the borough',
        mark: finished.length >= slots.length ? 'done' : 'new',
        kicker: 'Odd jobs',
        heading: 'Cars with a job in them',
        body: [
          'Some parked motors have a job in them: a note on the dash or a voice from the back seat. Get in and listen. Stay in to take the job; get out straight away to say no.',
          'You won’t find them on this page until you’ve done them. When one is close by it shows on the map as a “?”.',
          ...finished.map((s) => `${s.job.title}: ${s.job.done}`),
        ],
        meta: finished.length >= slots.length ? 'All six found and done.' : `${slots.length - finished.length} still out there.`,
      },
    ];
  }

  function introNote(): void {
    if (!open() || typeof window === 'undefined') return;
    ctx.services.tryGet('book')?.post({
      key: 'car-jobs-intro',
      from: 'Lenny the Ear',
      kind: 'tip',
      subject: 'Motors with something in them',
      text: 'Word to the wise: not every motor parked round here is just a motor. Some have a note on the dash, some have somebody in the back who needs a driver. Get in and listen — if you don’t fancy it, get straight out again.',
    });
  }

  return {
    id: 'carJobs',
    order: 538,
    init(c) {
      ctx = c;
      c.services.get('missions').registerActivities(() => (offering ? [offering] : []));
      c.hooks.register('carJobState', () => ({
        jobs: slots.map((s) => ({ id: s.job.id, x: s.spot.pos.x, z: s.spot.pos.z, vehicle: carOf(s)?.id ?? -1, done: done(s) })),
        offer: offer ? { id: offer.slot.job.id, left: +offer.left.toFixed(2) } : null,
      }));
      offs.push(
        c.events.on('newGameStarted', () => {
          offer = null;
          askedIn = -1;
          for (const s of slots) {
            const v = carOf(s);
            if (v && ctx.services.get('player').vehicle !== v) ctx.services.get('vehicles').despawn(v);
            s.vehicleId = -1;
          }
        }),
        c.events.on('missionCompleted', (e) => {
          if (!e.replay) queueMicrotask(introNote);
        }),
      );
    },
    start() {
      buildSlots();
      offs.push(ctx.services.tryGet('book')?.addJobsSource(jobsEntries) ?? (() => undefined));
      const u = ui();
      if (u) offs.push(u.addMarkerSource(markers));
      introNote();
    },
    fixedUpdate(dt) {
      if (!open() || ctx.state.mode !== 'playing') {
        offer = null;
        return;
      }
      keepParked();
      offers(dt);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
